import type { JSX } from 'react';
import { useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { InviteMemberDialog } from '@/features/membership/invite-member-dialog';
import { InvitationSuccessDialog } from '@/features/membership/invitation-success-dialog';
import {
  useCancelOrganizationInvitation,
  useOrganizationInvitations,
  useResendOrganizationInvitation,
} from '@/features/membership/hooks';
import { ROLE_LABELS, STATUS_LABELS } from '@/features/membership/labels';
import type {
  InviteToOrganizationResult,
  OrganizationInvitationStatus,
  OrganizationInvitationView,
} from '@/features/membership/types';
import { ApiError } from '@/lib/api/client';
import { useHasCapability } from '@/lib/capabilities';

function statusVariant(status: OrganizationInvitationStatus): 'default' | 'secondary' | 'outline' {
  if (status === 'ACCEPTED') {
    return 'default';
  }
  if (status === 'PENDING') {
    return 'secondary';
  }
  return 'outline';
}

/**
 * Teacher/Student invitation management for an Organization Admin (ADR-027,
 * Job 29 - "smallest coherent" surface: hosted on the existing Organization
 * profile page rather than a new admin dashboard, exactly the reuse
 * `OrganizationPage`'s own header already directs). Organization Admin
 * invitations are Platform-scoped (ADR-027, Decision 6) and have no UI here
 * - see Job 30's report in the Phase G-8 delivery notes.
 *
 * Capability-driven per row (Job 28's own convention, mirrored from
 * `GuardianSection`): inviting a Teacher requires `teachers:manage`,
 * inviting a Student requires `students:manage` - both already granted to
 * Organization Admin, and `students:manage` alone to Teacher. Resend/cancel
 * on a row use the same two capabilities, matching `membership.routes.ts`'s
 * own `requireAnyPermission(['teachers:manage', 'students:manage'])` gate.
 */
export function MembersSection(): JSX.Element {
  const { data: invitations, isLoading, isError } = useOrganizationInvitations();
  const [inviteRole, setInviteRole] = useState<'Teacher' | 'Student' | null>(null);
  const [successResult, setSuccessResult] = useState<InviteToOrganizationResult | null>(null);
  const canInviteTeacher = useHasCapability('teachers:manage');
  const canInviteStudent = useHasCapability('students:manage');
  const canManageAny = canInviteTeacher || canInviteStudent;
  const resend = useResendOrganizationInvitation();
  const cancel = useCancelOrganizationInvitation();

  // Organization Admin invitations are provisioned by a Platform Super
  // Admin through a separate Platform-scoped surface (ADR-027, Decision
  // 6) - this section only manages the two roles an Organization Admin
  // itself is entitled to invite.
  const rows = (invitations ?? []).filter((row) => row.targetRoleName !== 'Organization Admin');

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-4">
        <CardTitle>Anggota</CardTitle>
        {canManageAny && (
          <div className="flex gap-2">
            {canInviteTeacher && (
              <Button
                type="button"
                size="sm"
                onClick={() => {
                  setInviteRole('Teacher');
                }}
              >
                Undang Guru
              </Button>
            )}
            {canInviteStudent && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  setInviteRole('Student');
                }}
              >
                Undang Santri
              </Button>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        )}

        {isError && (
          <Alert variant="destructive">
            <AlertDescription>Gagal memuat daftar undangan.</AlertDescription>
          </Alert>
        )}

        {!isLoading && !isError && rows.length === 0 && (
          <p className="text-sm text-muted-foreground">Belum ada undangan.</p>
        )}

        {!isLoading && !isError && rows.length > 0 && (
          <ul className="flex flex-col gap-3">
            {rows.map((row) => (
              <InvitationRow
                key={row.id}
                invitation={row}
                canManage={row.targetRoleName === 'Teacher' ? canInviteTeacher : canInviteStudent}
                onResend={(result) => {
                  setSuccessResult(result);
                }}
              />
            ))}
          </ul>
        )}

        {(resend.isError || cancel.isError) && (
          <Alert variant="destructive" className="mt-3">
            <AlertDescription>
              {resend.error instanceof ApiError
                ? resend.error.message
                : cancel.error instanceof ApiError
                  ? cancel.error.message
                  : 'Terjadi kesalahan. Silakan coba lagi.'}
            </AlertDescription>
          </Alert>
        )}
      </CardContent>

      {inviteRole !== null && (
        <InviteMemberDialog
          role={inviteRole}
          open={inviteRole !== null}
          onOpenChange={(open) => {
            if (!open) {
              setInviteRole(null);
            }
          }}
          onInvited={setSuccessResult}
        />
      )}
      <InvitationSuccessDialog
        result={successResult}
        onClose={() => {
          setSuccessResult(null);
        }}
      />
    </Card>
  );
}

function InvitationRow({
  invitation,
  canManage,
  onResend,
}: {
  readonly invitation: OrganizationInvitationView;
  readonly canManage: boolean;
  readonly onResend: (result: InviteToOrganizationResult) => void;
}): JSX.Element {
  const resend = useResendOrganizationInvitation();
  const cancel = useCancelOrganizationInvitation();
  const isPending = invitation.status === 'PENDING';

  return (
    <li className="flex flex-col gap-2 rounded-md border border-border p-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 flex-col gap-1">
        <span className="truncate text-sm font-medium">{invitation.invitedEmail}</span>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="secondary">{ROLE_LABELS[invitation.targetRoleName]}</Badge>
          <Badge variant={statusVariant(invitation.status)}>
            {STATUS_LABELS[invitation.status]}
          </Badge>
        </div>
        <span className="text-xs text-muted-foreground">
          Diundang {new Date(invitation.invitedAt).toLocaleDateString('id-ID')}
          {invitation.acceptedAt !== null &&
            ` · Diterima ${new Date(invitation.acceptedAt).toLocaleDateString('id-ID')}`}
        </span>
      </div>
      {canManage && isPending && (
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={resend.isPending}
            onClick={() => {
              resend.mutate(invitation.id, { onSuccess: onResend });
            }}
          >
            {resend.isPending ? 'Mengirim...' : 'Kirim Ulang'}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={cancel.isPending}
            onClick={() => {
              cancel.mutate(invitation.id);
            }}
          >
            {cancel.isPending ? 'Membatalkan...' : 'Batalkan'}
          </Button>
        </div>
      )}
    </li>
  );
}
